import { useTranslation } from "@localization/useTranslation";
import { colorByRiskLevelIndicator, RiskLevelIndicator } from "../../../riskSettings/riskArea/riskAreaDialog/riskLevels/riskLevelIndicators";
import { buildCountAxisTicks } from "../../riskManagementReport/RiskManagementReport.utils";
import { buildRiskDistributionChartData, riskDistributionChartKeys } from "./RiskDistributionHistoryChart.utils";
import { RiskDistributionPoint } from "../RiskGovernanceOverview.types";

export interface RiskDistributionSegmentTooltip {
    color: string;
    label: string;
    count: string;
    share: string;
    period: string;
}

// Rotated month labels start overlapping around a year of history; below that they stay horizontal.
const maxHorizontalLabels = 12;

export function useRiskDistributionHistoryChart(
    points: Array<RiskDistributionPoint>,
    scale: number,
    riskLevels: Array<string>,
    riskLevelIndicators: Array<RiskLevelIndicator>,
    asOf: string | null
) {
    const { translateString, translateDate, translateUtcDateToShortMonthAndYear, translatePercentage } = useTranslation();

    const chartData = buildRiskDistributionChartData(points, scale);
    const keys = riskDistributionChartKeys(scale);

    // The final point is "now": labelled with the existing today key, or the snapshot date in as-of mode.
    const pointLabels = points.map((point) =>
        point.isCurrent ? (asOf ? translateDate(new Date(asOf)) : translateString("today")) : translateUtcDateToShortMonthAndYear(new Date(point.date))
    );

    const highestTotal = Math.max(1, ...chartData.map((bar) => Number(bar.total)));
    const { axisMax, tickValues } = buildCountAxisTicks(highestTotal);

    // The same vivid indicator color the matrix cells use, so a chart segment and its cell always agree.
    const segmentColor = (level: number) => colorByRiskLevelIndicator[riskLevelIndicators[level - 1] ?? RiskLevelIndicator.None];

    const segmentTooltip = (level: number, count: number, total: number, pointIndex: number): RiskDistributionSegmentTooltip => ({
        color: segmentColor(level),
        label: riskLevels[level - 1] ?? String(level),
        count: translateString("riskGovernanceDistributionTooltipCount", { count }),
        share: total > 0 ? translatePercentage(count / total) : "",
        period: pointLabels[pointIndex] ?? "",
    });

    return {
        chartData,
        keys,
        pointLabels,
        axisMax,
        tickValues,
        segmentColor,
        segmentTooltip,
        rotateTickLabels: points.length > maxHorizontalLabels,
    };
}
